import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchParts,
  fetchColors,
  fetchCategories,
  fetchSuppliers,
  createPart,
  updatePart,
  deletePart,
} from '../redux/slices/partsSlice';
import PartsTable from '../components/Tables/PartsTable';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { useSearch } from '../context/SearchContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { printTable } from '../utils/printTable';

const Parts = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.userType === 'admin';
  const {
    items: parts,
    colors,
    categories,
    suppliers,
    loading,
    error,
  } = useSelector((state) => state.parts);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterAvailability, setFilterAvailability] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '',
    category: '',
    supplier: '',
    sku: '',
    article_id: '',
    purchase_price: '',
    selling_price: '',
    quantity: '',
    min_stock_level: '5',
  });

  useEffect(() => {
    dispatch(fetchParts());
    dispatch(fetchColors());
    dispatch(fetchCategories());
    dispatch(fetchSuppliers());
  }, [dispatch]);

  // Filter parts
  const filteredParts = parts.filter((part) => {
    const matchesSearch =
      part.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.color?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.supplier?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !filterCategory || part.category === filterCategory;
    const matchesColor = !filterColor || part.color === filterColor;
    const matchesSupplier = !filterSupplier || part.supplier === filterSupplier;
    const matchesAvailability =
      !filterAvailability ||
      (filterAvailability === 'nla'
        ? !!part.is_nla
        : !part.is_nla);

    return (
      matchesSearch &&
      matchesCategory &&
      matchesColor &&
      matchesSupplier &&
      matchesAvailability
    );
  });

  const handlePrint = () => {
    printTable({
      title: t('parts.title'),
      subtitle: [filterCategory, filterColor, filterSupplier].filter(Boolean).join(', '),
      columns: [
        { header: t('partsTable.colPart') },
        { header: t('parts.color') },
        { header: t('parts.category') },
        { header: t('parts.supplier') },
        { header: t('partsTable.colOrderNumber') },
        { header: t('orderPartsReport.colStock'), align: 'right' },
        { header: t('partsTable.colPurchase'), align: 'right' },
        { header: t('partsTable.colSelling'), align: 'right' },
      ],
      rows: filteredParts.map((p) => [
        p.name,
        p.color,
        p.category,
        p.supplier,
        p.sku,
        p.quantity || 0,
        `${parseFloat(p.purchase_price || 0).toFixed(2)} CHF`,
        `${parseFloat(p.selling_price || 0).toFixed(2)} CHF`,
      ]),
    });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const dataToSend = {
      ...formData,
      purchase_price: parseFloat(formData.purchase_price) || 0,
      selling_price: parseFloat(formData.selling_price) || 0,
      quantity: parseInt(formData.quantity) || 0,
      min_stock_level: parseInt(formData.min_stock_level) || 5,
    };
    dispatch(createPart(dataToSend))
      .unwrap()
      .then(() => {
        setShowCreateModal(false);
        resetForm();
        // Refresh dropdown options
        dispatch(fetchColors());
        dispatch(fetchCategories());
        dispatch(fetchSuppliers());
      })
      .catch((err) => console.error('Failed to create part:', err));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '',
      category: '',
      supplier: '',
      sku: '',
      article_id: '',
      purchase_price: '',
      selling_price: '',
      quantity: '',
      min_stock_level: '5',
    });
  };

  const handleEdit = (id, data) => {
    dispatch(updatePart({ id, data }))
      .unwrap()
      .then(() => {
        // Refresh dropdown options in case new values were added
        dispatch(fetchColors());
        dispatch(fetchCategories());
        dispatch(fetchSuppliers());
      })
      .catch((err) => console.error('Failed to update part:', err));
  };

  const handleDelete = (id) => {
    if (window.confirm(t('parts.confirmDelete'))) {
      dispatch(deletePart(id)).catch((err) =>
        console.error('Failed to delete part:', err),
      );
    }
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterColor('');
    setFilterSupplier('');
    setFilterAvailability('');
  };

  const hasActiveFilters =
    filterCategory || filterColor || filterSupplier || filterAvailability;

  // Calculate stats
  const lowStockCount = parts.filter(
    (p) => !p.is_nla && p.quantity <= p.min_stock_level,
  ).length;
  const totalValue = parts.reduce(
    (sum, p) => sum + p.quantity * p.purchase_price,
    0,
  );

  if (error) {
    return (
      <div>
        <h1 className='text-3xl font-bold text-gray-900 mb-6'>{t('parts.title')}</h1>
        <div className='card bg-red-50 border border-red-200'>
          <p className='text-red-600'>{t('parts.errorLoading', { error })}</p>
          <button
            onClick={() => dispatch(fetchParts())}
            className='btn-primary mt-4'
          >
            {t('parts.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('parts.title')}</h1>
          <p className='text-gray-600 mt-1'>
            {t('parts.subtitle')}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={handlePrint} className='btn-secondary'>
            🖨️ {t('common.print')}
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className='btn-primary'
            >
              {t('parts.addPart')}
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-6'>
        <div className='card'>
          <p className='text-sm font-medium text-gray-600'>{t('parts.totalParts')}</p>
          <p className='text-3xl font-bold text-gray-900 mt-2'>
            {parts.length}
          </p>
        </div>
        <div className='card'>
          <p className='text-sm font-medium text-gray-600'>{t('parts.lowStock')}</p>
          <p className='text-3xl font-bold text-red-600 mt-2'>
            {lowStockCount}
          </p>
        </div>
        <div className='card'>
          <p className='text-sm font-medium text-gray-600'>{t('parts.totalValue')}</p>
          <p className='text-3xl font-bold text-green-600 mt-2'>
            CHF {totalValue.toFixed(2)}
          </p>
        </div>
        <div className='card'>
          <p className='text-sm font-medium text-gray-600'>{t('parts.filteredResults')}</p>
          <p className='text-3xl font-bold text-purple-600 mt-2'>
            {filteredParts.length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className='card mb-6'>
        <div className='flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 gap-4'>
          <div className='flex-1'>
            {searchTerm && (
              <p className='text-sm text-gray-600'>
                {t('parts.searchingFor')}{' '}
                <span className='font-medium text-gray-900'>
                  "{searchTerm}"
                </span>
              </p>
            )}
          </div>

          <div className='flex  items-center gap-3 font-xs'>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className='input-field w-auto'
            >
              <option value=''>{t('parts.allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={filterColor}
              onChange={(e) => setFilterColor(e.target.value)}
              className='input-field'
            >
              <option value=''>{t('parts.allColors')}</option>
              {colors.map((color) => (
                <option key={color} value={color}>
                  {color}
                </option>
              ))}
            </select>

            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className='input-field w-auto'
            >
              <option value=''>{t('parts.allSuppliers')}</option>
              {suppliers.map((supplier) => (
                <option key={supplier} value={supplier}>
                  {supplier}
                </option>
              ))}
            </select>

            <select
              value={filterAvailability}
              onChange={(e) => setFilterAvailability(e.target.value)}
              className='input-field w-auto'
            >
              <option value=''>{t('parts.allAvailability')}</option>
              <option value='available'>{t('parts.onlyAvailable')}</option>
              <option value='nla'>{t('parts.onlyNla')}</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className='btn-secondary whitespace-nowrap'
              >
                {t('parts.clearFilters')}
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filteredParts.length === 0 ? (
        <div className='card text-center py-12'>
          <div className='text-6xl mb-4'>🔩</div>
          <p className='text-xl font-semibold text-gray-900 mb-2'>
            {parts.length === 0 ? t('parts.noPartsYet') : t('parts.noResultsFound')}
          </p>
          <p className='text-gray-500 mb-6'>
            {parts.length === 0
              ? t('parts.createFirstPart')
              : t('parts.tryAdjusting')}
          </p>
          {parts.length === 0 && isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className='btn-primary'
            >
              {t('parts.addPart')}
            </button>
          )}
        </div>
      ) : (
        <PartsTable
          parts={filteredParts}
          onEdit={handleEdit}
          onDelete={isAdmin ? handleDelete : undefined}
        />
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t('parts.createNewPart')}
        size='lg'
      >
        <form onSubmit={handleCreate}>
          <div className='space-y-4'>
            {/* Row 1: Name & SKU */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.partName')}
                </label>
                <input
                  type='text'
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className='input-field'
                  placeholder={t('parts.enterPartName')}
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.skuOrderNumber')}
                </label>
                <input
                  type='text'
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({ ...formData, sku: e.target.value })
                  }
                  className='input-field'
                  placeholder={t('parts.skuPlaceholder')}
                />
              </div>
            </div>

            {/* Row 2: Color, Category, Supplier */}
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.color')}
                </label>
                <input
                  type='text'
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className='input-field'
                  placeholder={t('parts.colorPlaceholder')}
                  list='colors-list'
                />
                <datalist id='colors-list'>
                  {colors.map((color) => (
                    <option key={color} value={color} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.category')}
                </label>
                <input
                  type='text'
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className='input-field'
                  placeholder={t('parts.categoryPlaceholder')}
                  list='categories-list'
                />
                <datalist id='categories-list'>
                  {categories.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.supplier')}
                </label>
                <input
                  type='text'
                  value={formData.supplier}
                  onChange={(e) =>
                    setFormData({ ...formData, supplier: e.target.value })
                  }
                  className='input-field'
                  placeholder={t('parts.supplierPlaceholder')}
                  list='suppliers-list'
                />
                <datalist id='suppliers-list'>
                  {suppliers.map((supplier) => (
                    <option key={supplier} value={supplier} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Row 3: Article ID */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('parts.articleId')}
              </label>
              <input
                type='text'
                value={formData.article_id}
                onChange={(e) =>
                  setFormData({ ...formData, article_id: e.target.value })
                }
                className='input-field'
                placeholder={t('parts.articleIdPlaceholder')}
              />
            </div>

            {/* Row 4: Description */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('parts.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className='input-field'
                placeholder={t('parts.enterDescription')}
                rows='2'
              />
            </div>

            {/* Row 5: Prices */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.purchasePrice')}
                </label>
                <input
                  type='number'
                  step='0.01'
                  value={formData.purchase_price}
                  onChange={(e) =>
                    setFormData({ ...formData, purchase_price: e.target.value })
                  }
                  className='input-field'
                  placeholder='0.00'
                  min='0'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.sellingPrice')}
                </label>
                <input
                  type='number'
                  step='0.01'
                  value={formData.selling_price}
                  onChange={(e) =>
                    setFormData({ ...formData, selling_price: e.target.value })
                  }
                  className='input-field'
                  placeholder='0.00'
                  min='0'
                />
              </div>
            </div>

            {/* Row 6: Quantity & Min Stock */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.initialQuantity')}
                </label>
                <input
                  type='number'
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity: e.target.value })
                  }
                  className='input-field'
                  placeholder='0'
                  min='0'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('parts.minStockLevel')}
                </label>
                <input
                  type='number'
                  value={formData.min_stock_level}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_stock_level: e.target.value,
                    })
                  }
                  className='input-field'
                  placeholder='5'
                  min='0'
                />
              </div>
            </div>

            {/* Buttons */}
            <div className='flex justify-end space-x-3 pt-4'>
              <button
                type='button'
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className='btn-secondary'
              >
                {t('parts.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {t('parts.createPart')}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Parts;
